import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app, auth } from '../../../../firebase';
import { useAdminDashboardMetrics } from '../../hooks/useAdminDashboardMetrics';
import { formatearFecha } from '../../../../utils/dateUtils';
import StatCard from './StatCard';
import AdminAudienciaActividad from './AdminAudienciaActividad';

const MAX_ACCIONES = 8;
const MAX_RECIENTES = 5;

const overviewStatsInitial = {
  totalEmpresas: 0,
  empresasActivas: 0,
  solicitudesClientes: { total: 0, pendientes: 0, enviadas: 0, en_revision: 0, aprobadas: 0, rechazadas: 0 },
  solicitudesEmpresas: { total: 0, ingresada: 0, pendiente: 0, en_revision: 0, activada: 0, credenciales_asignadas: 0, rechazada: 0 },
  contactosEmpresa: {
    total: 0,
    hoy: 0,
    estaSemana: 0,
    visitasPorEmpresa: [],
    porMarca: [],
    porServicio: [],
    contactosUsuariosTotal: 0,
    contactosUsuariosHoy: 0,
    contactosUsuariosEstaSemana: 0,
    visitasProductosTotal: 0,
    visitasProductosHoy: 0,
    visitasProductosEstaSemana: 0,
    visitasProductosPorEmpresa: []
  },
  marcasMasBuscadasHomepage: [],
  serviciosMasBuscadosHomepage: [],
  inicioRapido: {
    chatUsuariosTotal: 0,
    chatUsuariosHoy: 0,
    chatUsuariosEstaSemana: 0,
    emailsCapturadosTotal: 0,
    emailsPendientesDashboard: 0,
    conversionDashboardTotal: 0,
    conversionRate: 0
  },
  solicitudesProductos: { pendientes: 0 },
  solicitudesCampanas: { pendientes: 0 },
  solicitudesClientesRecientes: [],
  solicitudesEmpresasRecientes: []
};

export default function AdminHome() {
  const { metrics, loading } = useAdminDashboardMetrics();
  const [acciones, setAcciones] = useState([]);
  const [accionesLoading, setAccionesLoading] = useState(true);
  const [overviewStats, setOverviewStats] = useState(overviewStatsInitial);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewPermissionErrors, setOverviewPermissionErrors] = useState([]);
  const [setAdminLoading, setSetAdminLoading] = useState(false);
  const [setAdminMessage, setSetAdminMessage] = useState('');
  const navigate = useNavigate();

  const loadOverview = useCallback(async () => {
    const stats = { ...overviewStatsInitial };
    const permissionErrors = [];
    const isPermissionError = (e) => e?.code === 'permission-denied' || (e?.message && e.message.includes('permission'));

    try {
      const empresasSnap = await getDocs(collection(db, 'empresas'));
      stats.totalEmpresas = empresasSnap.size;
      stats.empresasActivas = empresasSnap.docs.filter(d => {
        const e = d.data().estado;
        return e === 'activa' || e === 'Activa';
      }).length;
    } catch (e) { console.warn('Empresas:', e); }

    try {
      let solicitudesClienteSnap;
      try {
        solicitudesClienteSnap = await getDocs(query(collection(db, 'solicitudes_cliente'), orderBy('fecha_solicitud', 'desc')));
      } catch (_) {
        solicitudesClienteSnap = await getDocs(collection(db, 'solicitudes_cliente'));
      }
      const solicitudesCliente = solicitudesClienteSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      solicitudesCliente.sort((a, b) => {
        const fa = a.fecha_solicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
        const fb = b.fecha_solicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
        return fb - fa;
      });
      stats.solicitudesClientes.total = solicitudesCliente.length;
      stats.solicitudesClientes.enviadas = solicitudesCliente.filter(s => s.estado_general === 'enviada').length;
      stats.solicitudesClientes.en_revision = solicitudesCliente.filter(s => s.estado_general === 'en_revision').length;
      stats.solicitudesClientes.aprobadas = solicitudesCliente.filter(s => s.estado_general === 'aprobada').length;
      stats.solicitudesClientes.rechazadas = solicitudesCliente.filter(s => s.estado_general === 'rechazada').length;
      stats.solicitudesClientes.pendientes = solicitudesCliente.filter(s => s.estado_general === 'enviada' || s.estado_general === 'en_revision').length;
      stats.solicitudesClientesRecientes = solicitudesCliente.filter(s => s.estado_general === 'enviada' || s.estado_general === 'en_revision').slice(0, MAX_RECIENTES);
    } catch (e) {
      console.warn('Solicitudes cliente:', e);
      if (isPermissionError(e)) {
        permissionErrors.push('solicitudes_cliente');
        console.warn('[Admin] Sin rol admin. Ver aviso amarillo en la página y pulsa "Concederme rol admin", o configura ADMIN_SETUP_EMAILS en Firebase → docs/ADMIN-ROL-SETUP.md');
      }
    }

    try {
      const solicitudesEmpresaSnap = await getDocs(collection(db, 'solicitudes_empresa'));
      const solicitudesEmpresa = solicitudesEmpresaSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      solicitudesEmpresa.sort((a, b) => {
        const fa = a.fecha_solicitud?.toDate?.() || a.fecha_registro?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
        const fb = b.fecha_solicitud?.toDate?.() || b.fecha_registro?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
        return fb - fa;
      });
      stats.solicitudesEmpresas.total = solicitudesEmpresa.length;
      stats.solicitudesEmpresas.ingresada = solicitudesEmpresa.filter(s => s.estado === 'ingresada').length;
      stats.solicitudesEmpresas.pendiente = solicitudesEmpresa.filter(s => s.estado === 'pendiente').length;
      stats.solicitudesEmpresas.en_revision = solicitudesEmpresa.filter(s => s.estado === 'en_revision').length;
      stats.solicitudesEmpresas.activada = solicitudesEmpresa.filter(s => s.estado === 'activada').length;
      stats.solicitudesEmpresas.credenciales_asignadas = solicitudesEmpresa.filter(s => s.estado === 'credenciales_asignadas').length;
      stats.solicitudesEmpresas.rechazada = solicitudesEmpresa.filter(s => s.estado === 'rechazada').length;
      stats.solicitudesEmpresasRecientes = solicitudesEmpresa.filter(s =>
        s.estado === 'ingresada' || s.estado === 'pendiente' || s.estado === 'en_revision'
      ).slice(0, MAX_RECIENTES);
    } catch (e) {
      console.warn('Solicitudes empresa:', e);
      if (isPermissionError(e)) {
        permissionErrors.push('solicitudes_empresa');
        if (!permissionErrors.includes('solicitudes_cliente')) console.warn('[Admin] Sin rol admin. Ver aviso en la página o docs/ADMIN-ROL-SETUP.md');
      }
    }

    try {
      let contactosSnap;
      try {
        contactosSnap = await getDocs(
          query(collection(db, 'contactos_empresa'), where('userId', '==', null), orderBy('fechaCreacion', 'desc'))
        );
      } catch (_) {
        contactosSnap = await getDocs(query(collection(db, 'contactos_empresa'), where('userId', '==', null)));
      }
      const contactos = contactosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      contactos.sort((a, b) => {
        const fa = a.fechaCreacion?.toDate?.() || new Date(0);
        const fb = b.fechaCreacion?.toDate?.() || new Date(0);
        return fb - fa;
      });
      const ahora = new Date();
      const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      const inicioSemana = new Date(ahora);
      inicioSemana.setDate(inicioSemana.getDate() - 7);

      // Contamos "usuarios únicos" a partir de `anonymousSessionId` (y fallback a `doc.id` para docs antiguos).
      const sessionKey = (c) => c?.anonymousSessionId || c?.id;
      const usuariosAll = new Set();
      const usuariosHoy = new Set();
      const usuariosSemana = new Set();

      contactos.forEach((c) => {
        const key = sessionKey(c);
        if (!key) return;
        usuariosAll.add(key);
        const fechaMs = c.fechaCreacion?.toDate?.() || null;
        const fecha = fechaMs instanceof Date ? fechaMs : new Date(0);
        if (fecha >= inicioHoy) usuariosHoy.add(key);
        if (fecha >= inicioSemana) usuariosSemana.add(key);
      });

      stats.contactosEmpresa.contactosUsuariosTotal = usuariosAll.size;
      stats.contactosEmpresa.contactosUsuariosHoy = usuariosHoy.size;
      stats.contactosEmpresa.contactosUsuariosEstaSemana = usuariosSemana.size;

      const porMarca = {};
      const porServicio = {};
      contactos.forEach(c => {
        const marca = (c.marcaVehiculo || '').trim() || 'No especificada';
        porMarca[marca] = (porMarca[marca] || 0) + 1;
        const serv = (c.tipoServicio || '').trim() || 'Sin especificar';
        porServicio[serv] = (porServicio[serv] || 0) + 1;
      });
      stats.contactosEmpresa.porMarca = Object.entries(porMarca)
        .map(([marca, count]) => ({ marca, count }))
        .filter(x => x.marca !== 'No especificada')
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
      stats.contactosEmpresa.porServicio = Object.entries(porServicio)
        .map(([servicio, count]) => ({ servicio, count }))
        .filter(x => x.servicio !== 'Sin especificar')
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
    } catch (e) {
      console.warn('Contactos:', e);
      if (isPermissionError(e)) permissionErrors.push('contactos_empresa');
    }

    setOverviewPermissionErrors(permissionErrors);

    try {
      const visitasSnap = await getDocs(query(collection(db, 'visitas_perfil_empresa'), where('userId', '==', null)));
      const empresasSnap = await getDocs(collection(db, 'empresas'));
      const nombres = {};
      empresasSnap.docs.forEach(d => { nombres[d.id] = d.data().nombre || 'Sin nombre'; });

      const ahora = new Date();
      const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      const inicioSemana = new Date(ahora);
      inicioSemana.setDate(inicioSemana.getDate() - 7);

      const usuariosAll = new Set();
      const usuariosHoy = new Set();
      const usuariosSemana = new Set();
      const usuariosPorEmpresa = {};

      visitasSnap.docs.forEach(d => {
        const data = d.data();
        const eid = data.empresaId;
        if (!eid) return;
        const key = data.anonymousSessionId || d.id;
        if (!key) return;

        usuariosAll.add(key);
        if (!usuariosPorEmpresa[eid]) usuariosPorEmpresa[eid] = new Set();
        usuariosPorEmpresa[eid].add(key);

        const fecha = data.fecha?.toDate?.() || null;
        if (fecha) {
          if (fecha >= inicioHoy) usuariosHoy.add(key);
          if (fecha >= inicioSemana) usuariosSemana.add(key);
        }
      });

      stats.contactosEmpresa.total = usuariosAll.size;
      stats.contactosEmpresa.hoy = usuariosHoy.size;
      stats.contactosEmpresa.estaSemana = usuariosSemana.size;

      stats.contactosEmpresa.visitasPorEmpresa = Object.entries(usuariosPorEmpresa)
        .map(([empresaId, set]) => ({ empresaId, empresaNombre: nombres[empresaId] || empresaId, total: set.size }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8);
    } catch (e) { /* puede no existir visitas_perfil_empresa */ }

    try {
      const busquedasSnap = await getDocs(query(collection(db, 'busquedas_homepage'), where('userId', '==', null)));
      const marcasCount = {};
      const serviciosCount = {};
      busquedasSnap.docs.forEach(docSnap => {
        const d = docSnap.data();
        const tipo = d.tipo;
        const termino = (d.termino || '').trim();
        if (!termino || !tipo) return;
        const terminoNorm = termino.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const terminoDisplay = termino.charAt(0).toUpperCase() + termino.slice(1).toLowerCase();
        if (tipo === 'marca') {
          marcasCount[terminoNorm] = marcasCount[terminoNorm] || { termino: terminoDisplay, cantidad: 0 };
          marcasCount[terminoNorm].cantidad += 1;
        } else if (tipo === 'servicio') {
          serviciosCount[terminoNorm] = serviciosCount[terminoNorm] || { termino: terminoDisplay, cantidad: 0 };
          serviciosCount[terminoNorm].cantidad += 1;
        }
      });
      stats.marcasMasBuscadasHomepage = Object.values(marcasCount).sort((a, b) => b.cantidad - a.cantidad).slice(0, 10);
      stats.serviciosMasBuscadosHomepage = Object.values(serviciosCount).sort((a, b) => b.cantidad - a.cantidad).slice(0, 10);
    } catch (e) { console.warn('Busquedas homepage:', e); }

    try {
      const inicioRapidoSnap = await getDocs(query(collection(db, 'inicio_rapido_analytics'), limit(1000)));
      const ahora = new Date();
      const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      const inicioSemana = new Date(ahora);
      inicioSemana.setDate(inicioSemana.getDate() - 7);

      const chatSessions = [];
      let emailsCapturados = 0;
      let pendientesDashboard = 0;
      let accesosDashboard = 0;

      inicioRapidoSnap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const started = data.chatStarted === true || Number(data.userMessages || 0) > 0;
        if (started) chatSessions.push(data);
        if (data.emailCaptured === true) emailsCapturados += 1;
        if (data.emailCaptured === true && data.dashboardAccessed !== true) pendientesDashboard += 1;
        if (data.dashboardAccessed === true) accesosDashboard += 1;
      });

      stats.inicioRapido.chatUsuariosTotal = chatSessions.length;
      stats.inicioRapido.chatUsuariosHoy = chatSessions.filter((data) => {
        const fecha = data.chatStartedAt?.toDate?.() || data.updatedAt?.toDate?.() || null;
        return fecha && fecha >= inicioHoy;
      }).length;
      stats.inicioRapido.chatUsuariosEstaSemana = chatSessions.filter((data) => {
        const fecha = data.chatStartedAt?.toDate?.() || data.updatedAt?.toDate?.() || null;
        return fecha && fecha >= inicioSemana;
      }).length;
      stats.inicioRapido.emailsCapturadosTotal = emailsCapturados;
      stats.inicioRapido.emailsPendientesDashboard = pendientesDashboard;
      stats.inicioRapido.conversionDashboardTotal = accesosDashboard;
      stats.inicioRapido.conversionRate = emailsCapturados > 0
        ? Math.round((accesosDashboard / emailsCapturados) * 100)
        : 0;
    } catch (e) {
      console.warn('Inicio rapido analytics:', e);
      if (isPermissionError(e)) permissionErrors.push('inicio_rapido_analytics');
    }

    try {
      const visitasProductoSnap = await getDocs(query(collection(db, 'visitas_producto'), where('userId', '==', null)));
      const ahora = new Date();
      const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      const inicioSemana = new Date(ahora);
      inicioSemana.setDate(inicioSemana.getDate() - 7);

      const usuariosAll = new Set();
      const usuariosHoy = new Set();
      const usuariosSemana = new Set();
      const usuariosPorEmpresa = {};

      visitasProductoSnap.docs.forEach(d => {
        const data = d.data();
        const eid = data.empresaId;
        if (!eid) return;
        const key = data.anonymousSessionId || d.id;
        if (!key) return;

        usuariosAll.add(key);
        if (!usuariosPorEmpresa[eid]) usuariosPorEmpresa[eid] = new Set();
        usuariosPorEmpresa[eid].add(key);

        const fecha = data.fecha?.toDate?.() || null;
        if (fecha) {
          if (fecha >= inicioHoy) usuariosHoy.add(key);
          if (fecha >= inicioSemana) usuariosSemana.add(key);
        }
      });

      const empresasSnap = await getDocs(collection(db, 'empresas'));
      const nombres = {};
      empresasSnap.docs.forEach(d => { nombres[d.id] = d.data().nombre || 'Sin nombre'; });

      stats.contactosEmpresa.visitasProductosTotal = usuariosAll.size;
      stats.contactosEmpresa.visitasProductosHoy = usuariosHoy.size;
      stats.contactosEmpresa.visitasProductosEstaSemana = usuariosSemana.size;
      stats.contactosEmpresa.visitasProductosPorEmpresa = Object.entries(usuariosPorEmpresa)
        .map(([empresaId, set]) => ({ empresaId, empresaNombre: nombres[empresaId] || empresaId, total: set.size }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8);
    } catch (e) {
      // puede no existir visitas_producto o fallar por índices/permiso
    }

    try {
      const qProd = query(collection(db, 'solicitudes_producto'), where('estado', 'in', ['pendiente', 'en_revision']));
      stats.solicitudesProductos.pendientes = (await getDocs(qProd)).size;
    } catch (e) { /* sin índice */ }
    try {
      const qCamp = query(collection(db, 'solicitudes_campana'), where('estado', 'in', ['pendiente', 'en_revision']));
      stats.solicitudesCampanas.pendientes = (await getDocs(qCamp)).size;
    } catch (e) { /* sin índice */ }

    setOverviewStats(stats);
  }, []);

  const handleGrantAdmin = useCallback(async () => {
    setSetAdminLoading(true);
    setSetAdminMessage('');
    try {
      const fn = httpsCallable(getFunctions(app), 'setAdminRole');
      const res = await fn({});
      const data = res?.data;
      setSetAdminMessage(data?.message || 'Listo. Cierra sesión y vuelve a entrar.');
      setOverviewPermissionErrors([]);
      setTimeout(() => { setOverviewLoading(true); loadOverview().finally(() => setOverviewLoading(false)); }, 500);
    } catch (e) {
      setSetAdminMessage(e?.message || e?.code || 'Error. Tu email debe estar en ADMIN_SETUP_EMAILS (Firebase config) para el primer admin.');
    } finally {
      setSetAdminLoading(false);
    }
  }, [loadOverview]);

  useEffect(() => {
    loadOverview().finally(() => setOverviewLoading(false));
  }, [loadOverview]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const items = [];
      try {
        const [snapIngresada, snapPend] = await Promise.all([
          getDocs(query(collection(db, 'solicitudes_empresa'), where('estado', '==', 'ingresada'), limit(100))),
          getDocs(query(collection(db, 'solicitudes_empresa'), where('estado', '==', 'pendiente'), limit(100)))
        ]);
        const addItem = (d, tipo, sub) => {
          const data = d.data();
          const label = tipo === 'solicitud_ingresada'
            ? `Solicitud recibida: ${data.nombre_empresa || data.nombre || data.datos_solicitud?.nombreEmpresa || data.email || d.id}`
            : `Solicitud pendiente: ${data.nombre_empresa || data.nombre || data.datos_solicitud?.nombreEmpresa || data.email || d.id}`;
          items.push({ id: d.id, type: tipo, label, to: '/admin/solicitudes-registro', sub });
        };
        snapIngresada.docs.forEach((d) => addItem(d, 'solicitud_ingresada', 'Ingresada'));
        snapPend.docs.forEach((d) => {
          if (items.some((i) => i.id === d.id)) return;
          addItem(d, 'solicitud_pendiente', 'Revisar');
        });
      } catch (e) { /* sin índice */ }
      try {
        const qMatch = query(
          collection(db, 'solicitudes_empresa'),
          where('matchScore', '>=', 0.7),
          orderBy('matchScore', 'desc'),
          limit(50)
        );
        const snapMatch = await getDocs(qMatch);
        snapMatch.docs.forEach((d) => {
          if (items.length >= MAX_ACCIONES) return;
          const data = d.data();
          if (items.some((i) => i.id === d.id && i.type === 'solicitud_match')) return;
          items.push({
            id: d.id,
            type: 'solicitud_match',
            label: `Solicitud con coincidencias: ${data.datos_solicitud?.nombreEmpresa || data.nombre_empresa || d.id}`,
            to: '/admin/solicitudes-registro',
            sub: `Match ${Math.round((data.matchScore || 0) * 100)}%`
          });
        });
      } catch (e) { /* matchScore */ }
      try {
        const empSnap = await getDocs(query(collection(db, 'empresas'), limit(200)));
        empSnap.docs.forEach((d) => {
          if (items.length >= MAX_ACCIONES) return;
          const data = d.data();
          const sinTel = !data.telefono && !data.telefono_contacto;
          const sinDir = !data.direccion;
          const sinWeb = !data.website && !data.sitio_web;
          if (sinTel || sinDir || sinWeb) {
            items.push({
              id: d.id,
              type: 'empresa_incompleta',
              label: `Empresa incompleta: ${data.nombre || d.id}`,
              to: `/admin/editar-empresa/${d.id}`,
              sub: [sinTel && 'Tel', sinDir && 'Dir', sinWeb && 'Web'].filter(Boolean).join(', ')
            });
          }
        });
      } catch (e) { /* ignore */ }
      const ordered = items
        .filter((v, i, a) => a.findIndex((x) => x.id === v.id && x.type === v.type) === i)
        .slice(0, MAX_ACCIONES);
      if (!cancelled) setAcciones(ordered);
    };
    load().finally(() => { if (!cancelled) setAccionesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const s = overviewStats;
  const ovLoading = overviewLoading;

  return (
    <div className="space-y-6">
      {overviewPermissionErrors.length > 0 && (
        <div className="rounded-lg bg-amber-50 border-2 border-amber-400 p-4 text-amber-900 text-sm space-y-3">
          <p className="font-semibold">Sin permisos para cargar: {overviewPermissionErrors.join(', ')}</p>
          <p>Las reglas comprueban el documento <code>usuarios/&#123;tu UID&#125;</code>. El que debes editar es el que tiene <strong>ID = tu UID actual</strong>.</p>
          {auth.currentUser && (
            <p className="text-xs font-mono bg-amber-100 px-2 py-1 rounded">
              Tu UID actual: <strong>{auth.currentUser.uid}</strong> → en Firestore debe existir <code>usuarios/{auth.currentUser.uid}</code> con campo <code>rol</code> = <code>&quot;admin&quot;</code>.
            </p>
          )}
          <p>Tu usuario necesita <strong>rol admin</strong> (custom claim + documento en <code>usuarios</code>).</p>
          <p className="text-xs font-medium text-amber-800">Si antes tenías acceso y lo perdiste (recuperar rápido):</p>
          <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">
            <li>Firebase Console → <strong>Authentication</strong> → anota tu <strong>UID</strong>.</li>
            <li>Firestore → colección <code>usuarios</code> → documento con ese ID → campo <code>rol</code> = <code>&quot;admin&quot;</code> (o créalo si no existe).</li>
            <li>Recarga la página o cierra sesión y vuelve a entrar.</li>
          </ul>
          <p className="text-xs font-medium text-amber-800">O bien (primer admin o si prefieres el botón):</p>
          <ol className="list-decimal list-inside text-amber-800 space-y-1 text-xs">
            <li>Firebase → Functions → variable <code>ADMIN_SETUP_EMAILS</code> = tu email.</li>
            <li>Despliega: <code>firebase deploy --only functions:setAdminRole</code></li>
            <li>Pulsa el botón de abajo y luego <strong>cierra sesión y vuelve a entrar</strong>.</li>
          </ol>
          <button
            type="button"
            onClick={handleGrantAdmin}
            disabled={setAdminLoading}
            className="mt-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-60"
          >
            {setAdminLoading ? 'Espera...' : 'Concederme rol admin'}
          </button>
          {setAdminMessage && <p className="text-xs mt-1 text-amber-700">{setAdminMessage}</p>}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Panel Principal</h1>
        <button
          type="button"
          onClick={() => { setOverviewLoading(true); loadOverview().finally(() => setOverviewLoading(false)); }}
          disabled={ovLoading}
          className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-60 flex items-center gap-2"
        >
          {ovLoading ? <span className="animate-spin">⟳</span> : '🔄'}
          Actualizar estadísticas
        </button>
      </div>

      {/* KPIs de Audiencia y Conversión */}
      <section>
        <AdminAudienciaActividad />
      </section>

      {/* Métricas rápidas (hook) */}
      <section>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">Métricas rápidas</h2>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/2" />
                <div className="h-8 bg-slate-200 rounded w-1/3 mt-2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatCard title="Solicitudes pendientes" value={metrics.solicitudesPendientes} ctaLabel="Ver solicitudes" ctaTo="/admin/solicitudes-registro" icon="📋" />
            <StatCard title="Solicitudes con coincidencias" value={metrics.solicitudesConMatch} ctaLabel="Ver solicitudes" ctaTo="/admin/solicitudes-registro" icon="🔗" />
            <StatCard title="Empresas por verificar" value={metrics.empresasPorVerificar} ctaLabel="Ver empresas" ctaTo="/admin/empresas" icon="🏢" />
            <StatCard title="Clientes nuevos (mes)" value={metrics.clientesNuevosMes} ctaLabel="Ver clientes" ctaTo="/admin/clientes-kawescar" icon="👥" />
            <StatCard title="Consultas este mes" value={metrics.consultasMes} ctaLabel="Ver estadísticas" ctaTo="/admin/estadisticas" icon="📊" />
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Embudo Inicio Rápido</h2>
          <p className="text-sm text-slate-500 mt-1">
            Usuarios que conversan con el Agente Mecánico IA y avance hacia el dashboard principal.
          </p>
        </div>
        {ovLoading ? (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-800">{s.inicioRapido?.chatUsuariosTotal ?? 0}</div>
              <div className="text-blue-700 text-sm font-medium">Usuarios con chat</div>
              <div className="text-xs text-blue-500 mt-1">
                {s.inicioRapido?.chatUsuariosHoy ?? 0} hoy · {s.inicioRapido?.chatUsuariosEstaSemana ?? 0} últimos 7 días
              </div>
            </div>
            <div className="bg-cyan-50 border-l-4 border-cyan-500 rounded-lg p-4">
              <div className="text-2xl font-bold text-cyan-800">{s.inicioRapido?.emailsCapturadosTotal ?? 0}</div>
              <div className="text-cyan-700 text-sm font-medium">Correos capturados</div>
              <div className="text-xs text-cyan-500 mt-1">Magic link solicitado desde /copiloto-ia</div>
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
              <div className="text-2xl font-bold text-amber-800">{s.inicioRapido?.emailsPendientesDashboard ?? 0}</div>
              <div className="text-amber-700 text-sm font-medium">A un paso del dashboard</div>
              <div className="text-xs text-amber-500 mt-1">Entregaron correo y aún no completan acceso</div>
            </div>
            <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4">
              <div className="text-2xl font-bold text-emerald-800">{s.inicioRapido?.conversionRate ?? 0}%</div>
              <div className="text-emerald-700 text-sm font-medium">Conversión a dashboard</div>
              <div className="text-xs text-emerald-500 mt-1">{s.inicioRapido?.conversionDashboardTotal ?? 0} accesos completados</div>
            </div>
          </div>
        )}
      </section>

      {/* Estadísticas principales (overview) */}
      <section>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">Resumen general</h2>
        {ovLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 animate-pulse h-24" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <button type="button" onClick={() => navigate('/admin/empresas')} className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow text-left">
              <div className="text-2xl font-bold text-blue-800">{s.totalEmpresas}</div>
              <div className="text-blue-600 text-sm">Total Empresas</div>
            </button>
            <button type="button" onClick={() => navigate('/admin/empresas')} className="bg-green-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow text-left">
              <div className="text-2xl font-bold text-green-800">{s.empresasActivas}</div>
              <div className="text-green-600 text-sm">Empresas Activas</div>
            </button>
            <button type="button" onClick={() => navigate('/admin/clientes-kawescar?tab=solicitudes')} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:shadow-md transition-shadow text-left">
              <div className="text-2xl font-bold text-yellow-800">{s.solicitudesClientes?.pendientes ?? 0}</div>
              <div className="text-yellow-600 text-sm">Solicitudes Clientes</div>
              <div className="text-xs text-yellow-500 mt-1">{s.solicitudesClientes?.total ?? 0} total</div>
            </button>
            <button type="button" onClick={() => navigate('/admin/solicitudes-registro')} className="bg-orange-50 border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow text-left">
              <div className="text-2xl font-bold text-orange-800">{(s.solicitudesEmpresas?.ingresada ?? 0) + (s.solicitudesEmpresas?.pendiente ?? 0) + (s.solicitudesEmpresas?.en_revision ?? 0)}</div>
              <div className="text-orange-600 text-sm">Solicitudes Empresas</div>
              <div className="text-xs text-orange-500 mt-1">{s.solicitudesEmpresas?.total ?? 0} total · {(s.solicitudesEmpresas?.ingresada ?? 0)} ingresadas</div>
            </button>
            <button type="button" onClick={() => navigate('/admin/contactos-empresa')} className="bg-teal-50 border border-teal-200 rounded-lg p-4 hover:shadow-md transition-shadow text-left">
              <div className="text-2xl font-bold text-teal-800">{s.contactosEmpresa?.total ?? 0}</div>
              <div className="text-teal-600 text-sm">Visitas a perfil (usuarios únicos)</div>
              <div className="text-xs text-teal-500 mt-1">{s.contactosEmpresa?.hoy ?? 0} hoy</div>
            </button>
          </div>
        )}
      </section>

      {/* Solicitudes Productos y Campañas */}
      <section>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">Solicitudes pendientes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/solicitudes-productos')}
            className="bg-green-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow flex items-center justify-between text-left"
          >
            <div>
              <div className="text-2xl font-bold text-green-800">{ovLoading ? '...' : (s.solicitudesProductos?.pendientes ?? 0)}</div>
              <div className="text-green-600 text-sm font-medium">📦 Solicitudes Productos</div>
              <div className="text-xs text-green-500 mt-1">Pendientes de validación</div>
            </div>
            <span className="text-3xl">📦</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/solicitudes-campanas')}
            className="bg-amber-50 border border-amber-200 rounded-lg p-4 hover:shadow-md transition-shadow flex items-center justify-between text-left"
          >
            <div>
              <div className="text-2xl font-bold text-amber-800">{ovLoading ? '...' : (s.solicitudesCampanas?.pendientes ?? 0)}</div>
              <div className="text-amber-600 text-sm font-medium">📢 Solicitudes Campañas</div>
              <div className="text-xs text-amber-500 mt-1">Pendientes de validación</div>
            </div>
            <span className="text-3xl">📢</span>
          </button>
        </div>
      </section>

      {/* Contactos / Visitas al perfil y búsquedas Homepage */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <span>📞</span>
            Contactos y visitas al perfil
          </h2>
          <Link to="/admin/contactos-empresa" className="text-blue-600 hover:text-blue-800 text-sm font-medium">Ver reporte completo →</Link>
        </div>
        <div className="p-4">
          <p className="text-slate-600 text-sm mb-4">
            Métricas de usuarios no logueados: usuarios únicos que visitan perfiles y actividad desde Home. El formulario de contacto se muestra donde aplique.
          </p>
          {ovLoading ? (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="h-16 bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-16 bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-800">{s.contactosEmpresa?.total ?? 0}</div>
                  <div className="text-green-600 text-sm font-medium">Total visitas a perfil</div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-800">{s.contactosEmpresa?.hoy ?? 0}</div>
                  <div className="text-blue-600 text-sm font-medium">Hoy (visitas únicas)</div>
                </div>
                <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                  <div className="text-2xl font-bold text-indigo-800">{s.contactosEmpresa?.estaSemana ?? 0}</div>
                  <div className="text-indigo-600 text-sm font-medium">Últimos 7 días (visitas únicas)</div>
                </div>
              </div>
              <p className="text-xs text-slate-500 -mt-2 mb-4">
                Contactos (formulario) únicos: {s.contactosEmpresa?.contactosUsuariosTotal ?? 0} total · {s.contactosEmpresa?.contactosUsuariosHoy ?? 0} hoy · {s.contactosEmpresa?.contactosUsuariosEstaSemana ?? 0} últimos 7 días
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">🏢 Empresas con más visitas</h3>
                  {s.contactosEmpresa?.visitasPorEmpresa?.length > 0 ? (
                    <ul className="space-y-2">
                      {s.contactosEmpresa.visitasPorEmpresa.slice(0, 6).map((emp, idx) => (
                        <li key={emp.empresaId || idx} className="flex justify-between items-center py-1 border-b border-slate-100 last:border-0">
                          <Link to={emp.empresaId ? `/empresa/${emp.empresaId}` : '#'} target="_blank" className="text-blue-600 hover:underline text-sm truncate flex-1 mr-2">{emp.empresaNombre}</Link>
                          <span className="text-slate-600 font-semibold shrink-0">{emp.total}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-500 text-sm py-2">Sin visitas registradas</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">🚗 Marcas (al contactar)</h3>
                  {s.contactosEmpresa?.porMarca?.length > 0 ? (
                    <ul className="space-y-2">
                      {s.contactosEmpresa.porMarca.slice(0, 6).map((m, idx) => (
                        <li key={idx} className="flex justify-between items-center py-1 border-b border-slate-100 last:border-0">
                          <span className="text-slate-800 text-sm truncate flex-1">{m.marca}</span>
                          <span className="text-slate-600 font-semibold shrink-0">{m.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-500 text-sm py-2">Sin datos</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">🔧 Servicios más contactados</h3>
                  {s.contactosEmpresa?.porServicio?.length > 0 ? (
                    <ul className="space-y-2">
                      {s.contactosEmpresa.porServicio.slice(0, 6).map((sv, idx) => (
                        <li key={idx} className="flex justify-between items-center py-1 border-b border-slate-100 last:border-0">
                          <span className="text-slate-800 text-sm truncate flex-1" title={sv.servicio}>{sv.servicio}</span>
                          <span className="text-slate-600 font-semibold shrink-0">{sv.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-500 text-sm py-2">Sin datos</p>
                  )}
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">📦 Visitas a productos (usuarios únicos no logueados)</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                    Usuarios únicos: {s.contactosEmpresa?.visitasProductosTotal ?? 0}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                    Hoy: {s.contactosEmpresa?.visitasProductosHoy ?? 0}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                    Últimos 7 días: {s.contactosEmpresa?.visitasProductosEstaSemana ?? 0}
                  </span>
                </div>
                {s.contactosEmpresa?.visitasProductosPorEmpresa?.length > 0 ? (
                  <ul className="space-y-2">
                    {s.contactosEmpresa.visitasProductosPorEmpresa.slice(0, 6).map((emp, idx) => (
                      <li
                        key={emp.empresaId || idx}
                        className="flex justify-between items-center py-1 border-b border-slate-100 last:border-0"
                      >
                        <Link
                          to={emp.empresaId ? `/empresa/${emp.empresaId}` : '#'}
                          target="_blank"
                          className="text-blue-600 hover:underline text-sm truncate flex-1 mr-2"
                          rel="noopener noreferrer"
                        >
                          {emp.empresaNombre}
                        </Link>
                        <span className="text-slate-600 font-semibold shrink-0">{emp.total}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-500 text-sm py-2">Sin visitas a productos registradas</p>
                )}
              </div>
              <div className="mt-6 pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">🔍 Búsquedas en Homepage (usuarios no logueados)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">Marcas más buscadas</h4>
                    {s.marcasMasBuscadasHomepage?.length > 0 ? (
                      <ul className="space-y-1">
                        {s.marcasMasBuscadasHomepage.slice(0, 5).map((m, idx) => (
                          <li key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-700">{m.termino}</span>
                            <span className="font-medium text-slate-600">{m.cantidad}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-500 text-sm">Sin búsquedas</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">Servicios más buscados</h4>
                    {s.serviciosMasBuscadosHomepage?.length > 0 ? (
                      <ul className="space-y-1">
                        {s.serviciosMasBuscadosHomepage.slice(0, 5).map((srv, idx) => (
                          <li key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-700 truncate" title={srv.termino}>{srv.termino}</span>
                            <span className="font-medium text-slate-600 shrink-0">{srv.cantidad}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-500 text-sm">Sin búsquedas</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Solicitudes de Clientes y Empresas recientes */}
      <section>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">Solicitudes recientes</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <span>👥</span>
                Solicitudes de Clientes
                {s.solicitudesClientes?.pendientes > 0 && (
                  <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{s.solicitudesClientes.pendientes}</span>
                )}
              </h3>
              <Link to="/admin/clientes-kawescar?tab=solicitudes" className="text-blue-600 hover:text-blue-800 text-sm font-medium">Ver todas →</Link>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded p-2">
                  <div className="text-lg font-bold text-yellow-800">{ovLoading ? '...' : (s.solicitudesClientes?.pendientes ?? 0)}</div>
                  <div className="text-yellow-600 text-xs">Pendientes</div>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 rounded p-2">
                  <div className="text-lg font-bold text-green-800">{ovLoading ? '...' : (s.solicitudesClientes?.aprobadas ?? 0)}</div>
                  <div className="text-green-600 text-xs">Aprobadas</div>
                </div>
              </div>
              {s.solicitudesClientesRecientes?.length > 0 ? (
                <ul className="space-y-2">
                  {s.solicitudesClientesRecientes.map((sol) => (
                    <li
                      key={sol.id}
                      onClick={() => navigate('/admin/solicitudes-cliente')}
                      className="bg-slate-50 border border-slate-200 rounded-lg p-3 hover:bg-slate-100 cursor-pointer"
                    >
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 text-sm truncate">{sol.nombres} {sol.apellidos}</div>
                          <div className="text-xs text-slate-600 truncate">{sol.email}</div>
                          <div className="text-xs text-slate-500 mt-1">
                            {sol.fecha_solicitud?.toDate ? formatearFecha(sol.fecha_solicitud) : sol.fechaCreacion ? new Date(sol.fechaCreacion).toLocaleDateString('es-CL') : '—'}
                          </div>
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                          sol.estado_general === 'aprobada' ? 'bg-green-200 text-green-800' :
                          sol.estado_general === 'rechazada' ? 'bg-red-200 text-red-800' :
                          sol.estado_general === 'en_revision' ? 'bg-blue-200 text-blue-800' : 'bg-yellow-200 text-yellow-800'
                        }`}>{sol.estado_general || 'enviada'}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 text-sm py-4 text-center">No hay solicitudes pendientes de clientes</p>
              )}
              <Link to="/admin/clientes-kawescar?tab=solicitudes" className="mt-4 block w-full text-center bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">Ver todas las solicitudes de clientes</Link>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <span>🏢</span>
                Solicitudes de Empresas
                {((s.solicitudesEmpresas?.ingresada ?? 0) + (s.solicitudesEmpresas?.pendiente ?? 0) + (s.solicitudesEmpresas?.en_revision ?? 0)) > 0 && (
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {(s.solicitudesEmpresas?.ingresada ?? 0) + (s.solicitudesEmpresas?.pendiente ?? 0) + (s.solicitudesEmpresas?.en_revision ?? 0)}
                  </span>
                )}
              </h3>
              <Link to="/admin/solicitudes-registro" className="text-blue-600 hover:text-blue-800 text-sm font-medium">Ver todas →</Link>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-orange-50 border-l-4 border-orange-500 rounded p-2">
                  <div className="text-lg font-bold text-orange-800">{ovLoading ? '...' : (s.solicitudesEmpresas?.ingresada ?? 0) + (s.solicitudesEmpresas?.pendiente ?? 0) + (s.solicitudesEmpresas?.en_revision ?? 0)}</div>
                  <div className="text-orange-600 text-xs">Recibidas / Pendientes</div>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 rounded p-2">
                  <div className="text-lg font-bold text-green-800">{ovLoading ? '...' : (s.solicitudesEmpresas?.activada ?? 0) + (s.solicitudesEmpresas?.credenciales_asignadas ?? 0)}</div>
                  <div className="text-green-600 text-xs">Activadas</div>
                </div>
              </div>
              {s.solicitudesEmpresasRecientes?.length > 0 ? (
                <ul className="space-y-2">
                  {s.solicitudesEmpresasRecientes.map((sol) => (
                    <li
                      key={sol.id}
                      onClick={() => navigate('/admin/solicitudes-registro')}
                      className="bg-slate-50 border border-slate-200 rounded-lg p-3 hover:bg-slate-100 cursor-pointer"
                    >
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 text-sm truncate">{sol.nombre_empresa || sol.nombre || sol.datos_solicitud?.nombreEmpresa || sol.email || 'Sin nombre'}</div>
                          <div className="text-xs text-slate-600 truncate">{sol.email || sol.representante?.email || '—'}</div>
                          <div className="text-xs text-slate-500 mt-1">
                            {(sol.fecha_solicitud?.toDate || sol.fecha_registro?.toDate) ? formatearFecha(sol.fecha_solicitud || sol.fecha_registro) : sol.fechaCreacion ? new Date(sol.fechaCreacion).toLocaleDateString('es-CL') : '—'}
                          </div>
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                          sol.estado === 'activada' || sol.estado === 'credenciales_asignadas' ? 'bg-green-200 text-green-800' :
                          sol.estado === 'rechazada' ? 'bg-red-200 text-red-800' :
                          sol.estado === 'en_revision' ? 'bg-blue-200 text-blue-800' :
                          sol.estado === 'ingresada' ? 'bg-amber-200 text-amber-800' : 'bg-yellow-200 text-yellow-800'
                        }`}>{sol.estado || 'pendiente'}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 text-sm py-4 text-center">No hay solicitudes pendientes de empresas</p>
              )}
              <Link to="/admin/solicitudes-registro" className="mt-4 block w-full text-center bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 font-medium text-sm">Ver todas las solicitudes de empresas</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Acciones pendientes */}
      <section>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">Acciones pendientes</h2>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {accionesLoading ? (
            <div className="p-6 text-slate-500">Cargando...</div>
          ) : acciones.length === 0 ? (
            <div className="p-6 text-slate-500">No hay acciones pendientes.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {acciones.map((a) => (
                <li key={`${a.type}-${a.id}`}>
                  <button
                    type="button"
                    onClick={() => a.to && navigate(a.to)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                  >
                    <span className="text-sm font-medium text-slate-900">{a.label}</span>
                    {a.sub && <span className="text-xs text-slate-500">{a.sub}</span>}
                    {a.to && <span className="text-slate-400">→</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
